/** Fleet & quartermaster: vehicles, equipment (with full history), spare cars, inventory self-certification. */
import { useState } from 'react';
import { EQUIPMENT_CATEGORIES, EQUIPMENT_CATEGORY_LABEL, EQUIPMENT_STATUS_LABEL, FLEET_STATUS_LABEL, US_STATES, VEHICLE_COLORS, VEHICLE_MAKES, type EquipmentCategory, type EquipmentStatus, type FleetStatus } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, PageHeader, Spinner, useAction, useLoad, useNarrow } from '../shared/ui.tsx';
import { at, day, EmptyState, KPI, when } from './common.tsx';
import { ShutoffStatus } from './Shutoffs.tsx';
import { OilCard } from './Mileage.tsx';
import { FundingBox } from './Funding.tsx';

const FT: Record<string, 'ok' | 'info' | 'warn' | 'neutral' | 'bad'> = { assigned: 'ok', spare: 'info', out_of_service: 'warn', retired: 'neutral' };
const QT: Record<string, 'ok' | 'info' | 'warn' | 'neutral' | 'bad'> = { in_stock: 'neutral', issued: 'ok', installed: 'info', repair: 'warn', retired: 'neutral', lost: 'bad' };
const carName = (v: any) => `${v.unit_number} · ${[v.year, v.make, v.model].filter(Boolean).join(' ')}`;
const itemName = (q: any) => `${EQUIPMENT_CATEGORY_LABEL[q.category as EquipmentCategory] ?? q.category}${q.make || q.model ? ` — ${[q.make, q.model].filter(Boolean).join(' ')}` : ''}`;

function useStaff() {
  return useLoad(() => api.get<any>('/api/mail/people').then((x) => x.people as any[]), []);
}

export function FleetPage() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState<string>('');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/fleet?${status ? `status=${status}&` : ''}${q.trim() ? `q=${encodeURIComponent(q.trim())}` : ''}`), [status, q]);
  const c = r.data?.counts;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Fleet & equipment"
        title="Fleet"
        sub="Every vehicle: VIN, tag, PO, in-service date, who has it, what’s installed, and its full history."
        actions={
          r.data?.canManage && (
            <button className="btn primary" onClick={() => setAdding(true)}>
              Add vehicle
            </button>
          )
        }
      />
      {c && (
        <div className="ops-kpis">
          <KPI n={c.assigned} label="Assigned" on={status === 'assigned'} onClick={() => setStatus(status === 'assigned' ? '' : 'assigned')} />
          <KPI n={`${c.spare_free}/${c.spare}`} label="Spares available" tone={c.spare_free ? 'ok' : 'warn'} on={status === 'spare'} onClick={() => setStatus(status === 'spare' ? '' : 'spare')} />
          <KPI n={c.out_of_service} label="Out of service" tone={c.out_of_service ? 'warn' : undefined} on={status === 'out_of_service'} onClick={() => setStatus(status === 'out_of_service' ? '' : 'out_of_service')} />
          <KPI n={c.retired} label="Retired" on={status === 'retired'} onClick={() => setStatus(status === 'retired' ? '' : 'retired')} />
        </div>
      )}
      {adding && <VehicleForm onClose={() => setAdding(false)} onSaved={(id) => navigate(at(`/fleet/${id}`))} />}
      <div className="filterbar">
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Unit #, VIN, tag, make, deputy or PO" aria-label="Search fleet" />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.vehicles.length ? (
        <EmptyState icon="car" title="No vehicles">
          {r.data.canManage ? 'Add your fleet: unit number, VIN, tag, PO number and in-service date.' : undefined}
        </EmptyState>
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Unit</th>
                  <th>Vehicle</th>
                  <th>Tag</th>
                  <th>VIN</th>
                  <th>Status</th>
                  <th>Driver</th>
                  <th>Equipment</th>
                  <th>Mileage</th>
                </tr>
              </thead>
              <tbody>
                {r.data.vehicles.map((v: any) => (
                  <tr key={v.id} className="clickable" onClick={() => navigate(at(`/fleet/${v.id}`))}>
                    <td className="mono">
                      <strong>{v.unit_number}</strong>
                    </td>
                    <td>
                      {[v.year, v.make, v.model].filter(Boolean).join(' ')}
                      {v.color && <div className="small muted">{v.color}</div>}
                    </td>
                    <td className="mono">{v.tag ? `${v.tag} ${v.tag_state ?? ''}` : '—'}</td>
                    <td className="mono small">{v.vin}</td>
                    <td>
                      <Badge tone={FT[v.status]} dot>
                        {FLEET_STATUS_LABEL[v.status as FleetStatus]}
                      </Badge>
                      {v.removed_on && (
                        <div className="small muted">
                          {v.sold_for != null ? `Sold $${Number(v.sold_for).toLocaleString()}` : v.auction_on ? `Auction ${day(v.auction_on)}` : v.disposal_resolution ? `Res. ${v.disposal_resolution}` : `Out ${day(v.removed_on)}`}
                        </div>
                      )}
                      {v.grant_funded && <div className="small muted">Grant {v.grant_number}</div>}
                    </td>
                    <td>{v.temp_name ? <>{v.temp_name} <Badge tone="info">using spare</Badge></> : v.assigned_name ?? <span className="muted">—</span>}</td>
                    <td>{v.equipment || '—'}</td>
                    <td>{v.mileage != null ? v.mileage.toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** "Bought with a grant?" — and if so, the grant number. */
function GrantQuestion({ what, funded, number, onChange }: { what: string; funded: boolean | null; number: string; onChange: (funded: boolean | null, number: string) => void }) {
  return (
    <div className="grant-q">
      <span className="lbl">Was this {what} bought with a grant?</span>
      <div className="row gap-sm wrap">
        <div className="seg-ctl" role="radiogroup" aria-label={`Was this ${what} bought with a grant?`}>
          {(
            [
              [false, 'No'],
              [true, 'Yes, a grant'],
            ] as const
          ).map(([v, l]) => (
            <button key={l} type="button" role="radio" aria-checked={funded === v} className={funded === v ? 'on' : ''} onClick={() => onChange(v, v ? number : '')}>
              {l}
            </button>
          ))}
        </div>
        {funded && <input className="mono grant-no" value={number} onChange={(e) => onChange(true, e.target.value)} placeholder="Grant number" aria-label="Grant number" autoFocus />}
      </div>
    </div>
  );
}

const grantText = (x: any) => (x.grant_funded ? `Yes — grant ${x.grant_number}` : x.grant_funded === false ? 'No' : '—');

function VehicleForm({ v, onClose, onSaved }: { v?: any; onClose: () => void; onSaved: (id: string) => void }) {
  const staff = useStaff();
  const [f, setF] = useState<Record<string, any>>(() => ({
    unitNumber: v?.unit_number ?? '', vin: v?.vin ?? '', year: v?.year ?? '', make: v?.make ?? '', model: v?.model ?? '', color: v?.color ?? '', tag: v?.tag ?? '', tagState: v?.tag_state ?? 'SC',
    poNumber: v?.po_number ?? '', inServiceOn: v?.in_service_on ? String(v.in_service_on).slice(0, 10) : '', mileage: v?.mileage ?? '', notes: v?.notes ?? '', assignedTo: v?.assigned_to ?? '', oldVin: false,
    grantFunded: v?.grant_funded ?? null, grantNumber: v?.grant_number ?? '',
  }));
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const a = useAction();
  const save = () =>
    a.run(async () => {
      const body = { ...f, year: f.year ? Number(f.year) : null, mileage: f.mileage === '' ? null : Number(f.mileage), assignedTo: f.assignedTo || null };
      if (!v) delete (body as any).oldVin;
      if (v) {
        const { assignedTo, ...rest } = body;
        await api.post(`/api/fleet/${v.id}`, rest);
        onSaved(v.id);
      } else onSaved((await api.post<any>('/api/fleet', body)).id);
    });
  return (
    <div className="card stack" role="dialog" aria-label={v ? 'Edit vehicle' : 'Add vehicle'}>
      <h3>{v ? `Edit unit ${v.unit_number}` : 'Add a vehicle'}</h3>
      <div className="grid-4">
        <label>
          Unit #
          <input value={f.unitNumber} onChange={set('unitNumber')} />
        </label>
        <label className="span-2">
          VIN
          <input className="mono" value={f.vin} maxLength={17} onChange={(e) => setF({ ...f, vin: e.target.value.toUpperCase() })} />
        </label>
        <label>
          Year
          <input inputMode="numeric" value={f.year} onChange={set('year')} />
        </label>
        <label>
          Make
          <input list="makes" value={f.make} onChange={set('make')} />
          <datalist id="makes">
            {VEHICLE_MAKES.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </label>
        <label>
          Model
          <input value={f.model} onChange={set('model')} placeholder="Police Interceptor Utility" />
        </label>
        <label>
          Color
          <select value={f.color} onChange={set('color')}>
            <option value="">—</option>
            {VEHICLE_COLORS.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <label>
          Tag
          <span className="row gap-sm">
            <input className="mono" value={f.tag} onChange={(e) => setF({ ...f, tag: e.target.value.toUpperCase() })} />
            <select value={f.tagState} onChange={set('tagState')} aria-label="Tag state">
              {US_STATES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </span>
        </label>
        <label>
          PO #
          <input value={f.poNumber} onChange={set('poNumber')} />
        </label>
        <label>
          In service
          <input type="date" value={f.inServiceOn} onChange={set('inServiceOn')} />
        </label>
        <label>
          Mileage
          <input inputMode="numeric" value={f.mileage} onChange={set('mileage')} />
        </label>
        {!v && (
          <label>
            Assigned to
            <select value={f.assignedTo} onChange={set('assignedTo')}>
              <option value="">Nobody — it’s a spare</option>
              {(staff.data ?? []).map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <label>
        Notes
        <textarea rows={2} value={f.notes} onChange={set('notes')} />
      </label>
      <GrantQuestion what="vehicle" funded={f.grantFunded} number={f.grantNumber} onChange={(grantFunded, grantNumber) => setF({ ...f, grantFunded, grantNumber })} />
      <label className="check small">
        <input type="checkbox" checked={f.oldVin} onChange={(e) => setF({ ...f, oldVin: e.target.checked })} /> Pre-1981 VIN (shorter, no check digit)
      </label>
      <div className="row gap-sm">
        <button className="btn primary" disabled={a.busy} onClick={save}>
          Save
        </button>
        <button className="btn ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
      <ErrorBox error={a.error} />
    </div>
  );
}

function History({ rows }: { rows: any[] }) {
  return rows.length === 0 ? (
    <p className="muted">No history yet.</p>
  ) : (
    <ol className="timeline compact">
      {rows.map((e) => (
        <li key={e.id}>
          <span className="tl-dot" aria-hidden />
          <div>
            <strong>{e.summary}</strong>
            <div className="small muted">
              {when(e.at, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} · {e.by_name}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function VehicleDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/fleet/${id}`), [id]);
  const staff = useStaff();
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [who, setWho] = useState('');
  const [reason, setReason] = useState('');
  const a = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const v = r.data.vehicle;
  const manage = r.data.canManage;
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<Link to={at('/fleet')}>Fleet</Link>}
        title={`Unit ${v.unit_number}`}
        sub={[v.year, v.color, v.make, v.model].filter(Boolean).join(' ')}
        actions={
          manage && !editing && (
            <button className="btn" onClick={() => setEditing(true)}>
              Edit details
            </button>
          )
        }
      />
      {editing && <VehicleForm v={v} onClose={() => setEditing(false)} onSaved={() => (setEditing(false), r.reload())} />}
      <div className="grid-2">
        <Card title="Vehicle">
          <dl className="kv">
            <dt>Status</dt>
            <dd>
              <Badge tone={FT[v.status]} dot>
                {FLEET_STATUS_LABEL[v.status as FleetStatus]}
              </Badge>
            </dd>
            <dt>Assigned to</dt>
            <dd>{v.assigned_name ?? '—'}</dd>
            {v.temp_name && (
              <>
                <dt>Spare in use by</dt>
                <dd>
                  {v.temp_name} since {when(v.temp_since)}
                  {v.temp_reason && <div className="small muted">{v.temp_reason}</div>}
                </dd>
              </>
            )}
            <dt>VIN</dt>
            <dd className="mono">{v.vin}</dd>
            <dt>Tag</dt>
            <dd className="mono">{v.tag ? `${v.tag} (${v.tag_state ?? '—'})` : '—'}</dd>
            <dt>PO #</dt>
            <dd>{v.po_number ?? '—'}</dd>
            <dt>In service</dt>
            <dd>{day(v.in_service_on)}</dd>
            <dt>Mileage</dt>
            <dd>{v.mileage != null ? v.mileage.toLocaleString() : '—'}</dd>
            <dt>Grant</dt>
            <dd>{grantText(v)}</dd>
            {v.notes && (
              <>
                <dt>Notes</dt>
                <dd className="pre">{v.notes}</dd>
              </>
            )}
          </dl>
          {manage && (
            <div className="stack-sm">
              <div className="row gap-sm wrap">
                {(['assigned', 'spare', 'out_of_service', 'retired'] as FleetStatus[])
                  .filter((s) => s !== v.status || s === 'assigned')
                  .map((s) => (
                    <button key={s} className={`btn sm ${status === s ? 'primary' : ''}`} onClick={() => setStatus(s)}>
                      {s === 'assigned' ? (v.status === 'assigned' ? 'Reassign' : 'Assign to a deputy') : s === 'spare' ? 'Make it a spare' : s === 'out_of_service' ? 'Out of service' : 'Retire'}
                    </button>
                  ))}
              </div>
              {status && (
                <div className="row gap-sm wrap">
                  {status === 'assigned' && (
                    <select value={who} onChange={(e) => setWho(e.target.value)} aria-label="Assign to">
                      <option value="">Choose…</option>
                      {(staff.data ?? []).map((p: any) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (optional)" aria-label="Reason" />
                  <button
                    className="btn primary"
                    disabled={a.busy || (status === 'assigned' && !who)}
                    onClick={() => a.run(async () => (await api.post(`/api/fleet/${id}`, status === 'assigned' ? { status, assignedTo: who, reason } : { status, reason }), setStatus(null), setReason(''), r.reload()))}
                  >
                    Save
                  </button>
                </div>
              )}
              <ErrorBox error={a.error} />
            </div>
          )}
        </Card>
        <Card title="Installed equipment">
          {r.data.equipment.length === 0 ? (
            <p className="muted">Nothing installed.</p>
          ) : (
            <ul className="plain">
              {r.data.equipment.map((q: any) => (
                <li key={q.id}>
                  <Link to={at(`/equipment/${q.id}`)}>{itemName(q)}</Link>
                  <span className="small muted mono"> {q.serial ? `SN ${q.serial}` : ''} {q.asset_tag ? `· ${q.asset_tag}` : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      <FundingBox kind="vehicle" id={v.id} funding={v.funding} refText={v.funding_ref} canEdit={manage} onSaved={r.reload} sold={v.sold_for != null} />
      <OilCard v={v} canManage={manage} onSaved={r.reload} />
      <CountyItems v={v} canManage={manage} onSaved={r.reload} />
      {(v.status === 'retired' || v.sold_for != null) && <ShutoffStatus kind="vehicle" id={v.id} />}
      <Disposal v={v} canManage={manage} onSaved={r.reload} />
      <Card title="History">
        <History rows={r.data.history} />
      </Card>
    </div>
  );
}

/** What the county shuts off when the car leaves: RFID tag (out of the fleet), insurance and fuel card (sold). */
function CountyItems({ v, canManage, onSaved }: { v: any; canManage: boolean; onSaved: () => void }) {
  const [f, setF] = useState<{ rfidTag: string; insurancePolicy: string; fuelCard: string } | null>(null);
  const a = useAction();
  return (
    <Card
      title="RFID tag, insurance & fuel card"
      actions={
        canManage &&
        !f && (
          <button className="btn sm" onClick={() => setF({ rfidTag: v.rfid_tag ?? '', insurancePolicy: v.insurance_policy ?? '', fuelCard: v.fuel_card ?? '' })}>
            Edit
          </button>
        )
      }
    >
      {f ? (
        <div className="stack sm">
          <div className="grid-3">
            <label>
              RFID tag #
              <input value={f.rfidTag} onChange={(e) => setF({ ...f, rfidTag: e.target.value })} />
            </label>
            <label>
              Insurance policy #
              <input value={f.insurancePolicy} onChange={(e) => setF({ ...f, insurancePolicy: e.target.value })} />
            </label>
            <label>
              Fuel card #
              <input value={f.fuelCard} onChange={(e) => setF({ ...f, fuelCard: e.target.value })} />
            </label>
          </div>
          <div className="row gap-sm">
            <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/fleet/${v.id}`, { rfidTag: f.rfidTag || null, insurancePolicy: f.insurancePolicy || null, fuelCard: f.fuelCard || null }), setF(null), onSaved()))}>
              Save
            </button>
            <button className="btn ghost" onClick={() => setF(null)}>
              Cancel
            </button>
          </div>
          <ErrorBox error={a.error} />
        </div>
      ) : (
        <>
          <dl className="kv">
            <dt>RFID tag</dt>
            <dd className="mono">{v.rfid_tag ?? '—'}</dd>
            <dt>Insurance policy</dt>
            <dd className="mono">{v.insurance_policy ?? '—'}</dd>
            <dt>Fuel card</dt>
            <dd className="mono">{v.fuel_card ?? '—'}</dd>
          </dl>
          <p className="small muted">When the car leaves the fleet, the RFID contact is emailed to shut the tag off. When it’s sold, the county is emailed to cancel the insurance and fuel card.</p>
        </>
      )}
    </Card>
  );
}

/** Taking a vehicle out of the fleet: removed from service → resolution to sell → auction. */
function Disposal({ v, canManage, onSaved }: { v: any; canManage: boolean; onSaved: () => void }) {
  const d10 = (x: any) => (x ? String(x).slice(0, 10) : '');
  const start = () => ({ removedOn: d10(v.removed_on), disposalResolution: v.disposal_resolution ?? '', auctionOn: d10(v.auction_on), soldFor: v.sold_for != null ? String(v.sold_for) : '', disposalNotes: v.disposal_notes ?? '' });
  const [f, setF] = useState(start);
  const [editing, setEditing] = useState(false);
  const a = useAction();
  const has = v.removed_on || v.disposal_resolution || v.auction_on;
  if (!has && !editing) {
    if (!canManage) return null;
    return (
      <div className="row gap-sm">
        <button className="btn" onClick={() => (setF({ ...start(), removedOn: new Date().toLocaleDateString('en-CA') }), setEditing(true))}>
          Take out of service for disposal…
        </button>
      </div>
    );
  }
  const set = (k: keyof ReturnType<typeof start>) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const steps: [string, string | null, string][] = [
    ['Removed from service', v.removed_on ? day(v.removed_on) : null, 'removed'],
    ['Resolution to sell', v.disposal_resolution ? `# ${v.disposal_resolution}` : null, 'resolution'],
    ['Taken to auction', v.auction_on ? day(v.auction_on) : null, 'auction'],
    ['Sold', v.sold_for != null ? `$${Number(v.sold_for).toLocaleString()}` : null, 'sold'],
  ];
  return (
    <Card
      title="Disposal"
      actions={
        canManage && !editing && (
          <button className="btn sm" onClick={() => (setF(start()), setEditing(true))}>
            Update
          </button>
        )
      }
    >
      {!editing ? (
        <>
          <ol className="disposal-steps">
            {steps.map(([l, val, k]) => (
              <li key={k} className={val ? 'done' : ''}>
                <span className="small muted">{l}</span>
                <strong>{val ?? '—'}</strong>
              </li>
            ))}
          </ol>
          {v.disposal_notes && <p className="small pre">{v.disposal_notes}</p>}
          {v.grant_funded && <p className="small warn-text">Grant-funded (grant {v.grant_number}) — check the grant’s rules on disposal and proceeds before selling.</p>}
        </>
      ) : (
        <div className="stack sm">
          <div className="grid-4">
            <label>
              Removed from service
              <input type="date" value={f.removedOn} onChange={set('removedOn')} />
            </label>
            <label>
              Resolution # to sell
              <input value={f.disposalResolution} onChange={set('disposalResolution')} placeholder="e.g. 2026-41" />
            </label>
            <label>
              Date taken to auction
              <input type="date" value={f.auctionOn} onChange={set('auctionOn')} />
            </label>
            <label>
              Sold for ($, optional)
              <input inputMode="numeric" value={f.soldFor} onChange={(e) => setF({ ...f, soldFor: e.target.value.replace(/\D/g, '') })} />
            </label>
          </div>
          <label>
            Notes
            <textarea rows={2} value={f.disposalNotes} onChange={set('disposalNotes')} placeholder="Auction house, lot number, buyer, title transferred…" />
          </label>
          {v.status !== 'retired' && f.removedOn && <p className="small muted">Saving marks Unit {v.unit_number} retired and takes it off {v.assigned_name ?? 'its deputy'}.</p>}
          <div className="row gap-sm">
            <button
              className="btn primary"
              disabled={a.busy || !f.removedOn}
              onClick={() =>
                a.run(async () => {
                  await api.post(`/api/fleet/${v.id}`, { removedOn: f.removedOn || null, disposalResolution: f.disposalResolution || null, auctionOn: f.auctionOn || null, soldFor: f.soldFor === '' ? null : Number(f.soldFor), disposalNotes: f.disposalNotes || null });
                  setEditing(false);
                  onSaved();
                })
              }
            >
              Save
            </button>
            <button className="btn ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
          <ErrorBox error={a.error} />
        </div>
      )}
    </Card>
  );
}

export function EquipmentPage() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [status, setStatus] = useState('');
  const [adding, setAdding] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/equipment?q=${encodeURIComponent(q.trim())}&category=${cat}&status=${status}`), [q, cat, status]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Fleet & equipment"
        title="Equipment"
        sub="Radios, radar, cameras, weapons, vests — with serial numbers, who has each one, and where it’s been."
        actions={
          r.data?.canManage && (
            <button className="btn primary" onClick={() => setAdding(true)}>
              Add equipment
            </button>
          )
        }
      />
      {adding && <ItemForm onClose={() => setAdding(false)} onSaved={(id) => navigate(at(`/equipment/${id}`))} />}
      <div className="filterbar">
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Serial, asset tag, make, model, person or unit #" aria-label="Search equipment" />
        <select value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
          <option value="">All kinds</option>
          {EQUIPMENT_CATEGORIES.map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">In use or in stock</option>
          {Object.entries(EQUIPMENT_STATUS_LABEL).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.items.length ? (
        <EmptyState icon="radio" title="No equipment found" />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Serial</th>
                  <th>Asset tag</th>
                  <th>Status</th>
                  <th>Where</th>
                  <th>PO #</th>
                </tr>
              </thead>
              <tbody>
                {r.data.items.map((x: any) => (
                  <tr key={x.id} className="clickable" onClick={() => navigate(at(`/equipment/${x.id}`))}>
                    <td>
                      {itemName(x)}
                      {x.description && <div className="small muted">{x.description}</div>}
                    </td>
                    <td className="mono">{x.serial ?? '—'}</td>
                    <td className="mono">{x.asset_tag ?? '—'}</td>
                    <td>
                      <Badge tone={QT[x.status]}>{EQUIPMENT_STATUS_LABEL[x.status as EquipmentStatus]}</Badge>
                    </td>
                    <td>{x.assigned_name ?? (x.unit_number ? `Unit ${x.unit_number}` : '—')}</td>
                    <td>{x.po_number ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemForm({ it, onClose, onSaved }: { it?: any; onClose: () => void; onSaved: (id: string) => void }) {
  const [f, setF] = useState<Record<string, any>>(() => ({
    grantFunded: it?.grant_funded ?? null, grantNumber: it?.grant_number ?? '',
    category: it?.category ?? 'radio_portable', description: it?.description ?? '', make: it?.make ?? '', model: it?.model ?? '', serial: it?.serial ?? '', assetTag: it?.asset_tag ?? '',
    poNumber: it?.po_number ?? '', inServiceOn: it?.in_service_on ? String(it.in_service_on).slice(0, 10) : '', notes: it?.notes ?? '',
  }));
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const a = useAction();
  return (
    <div className="card stack" role="dialog" aria-label={it ? 'Edit equipment' : 'Add equipment'}>
      <h3>{it ? 'Edit equipment' : 'Add equipment'}</h3>
      <div className="grid-4">
        <label>
          Kind
          <select value={f.category} onChange={set('category')}>
            {EQUIPMENT_CATEGORIES.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          Make
          <input value={f.make} onChange={set('make')} placeholder="Motorola" />
        </label>
        <label>
          Model
          <input value={f.model} onChange={set('model')} placeholder="APX 6000" />
        </label>
        <label>
          Serial #
          <input className="mono" value={f.serial} onChange={set('serial')} />
        </label>
        <label>
          Asset tag
          <input className="mono" value={f.assetTag} onChange={set('assetTag')} />
        </label>
        <label>
          PO #
          <input value={f.poNumber} onChange={set('poNumber')} />
        </label>
        <label>
          In service
          <input type="date" value={f.inServiceOn} onChange={set('inServiceOn')} />
        </label>
        <label>
          Description
          <input value={f.description} onChange={set('description')} placeholder="e.g. Size L, exp. 2030" />
        </label>
      </div>
      <GrantQuestion what={EQUIPMENT_CATEGORY_LABEL[f.category as EquipmentCategory]?.toLowerCase() ?? 'item'} funded={f.grantFunded} number={f.grantNumber} onChange={(grantFunded, grantNumber) => setF({ ...f, grantFunded, grantNumber })} />
      <label>
        Notes
        <textarea rows={2} value={f.notes} onChange={set('notes')} />
      </label>
      <div className="row gap-sm">
        <button
          className="btn primary"
          disabled={a.busy}
          onClick={() =>
            a.run(async () => {
              if (it) (await api.post(`/api/equipment/${it.id}`, f), onSaved(it.id));
              else onSaved((await api.post<any>('/api/equipment', f)).id);
            })
          }
        >
          Save
        </button>
        <button className="btn ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
      <ErrorBox error={a.error} />
    </div>
  );
}

export function EquipmentDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/equipment/${id}`), [id]);
  const staff = useStaff();
  const cars = useLoad(() => api.get<any>('/api/fleet').then((x) => x.vehicles as any[]).catch(() => []), []);
  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState<string | null>(null);
  const [target, setTarget] = useState('');
  const [note, setNote] = useState('');
  const a = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const x = r.data.item;
  const MOVES: [string, string][] = [
    ['person', 'Issue to a person'],
    ['vehicle', 'Install in a vehicle'],
    ['stock', 'Turn in to stock'],
    ['repair', 'Send for repair'],
    ['lost', 'Report lost / stolen'],
    ['retired', 'Retire / surplus'],
  ];
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<Link to={at('/equipment')}>Equipment</Link>}
        title={itemName(x)}
        sub={[x.serial && `SN ${x.serial}`, x.asset_tag && `Asset ${x.asset_tag}`].filter(Boolean).join(' · ')}
        actions={
          r.data.canManage && !editing && (
            <button className="btn" onClick={() => setEditing(true)}>
              Edit details
            </button>
          )
        }
      />
      {editing && <ItemForm it={x} onClose={() => setEditing(false)} onSaved={() => (setEditing(false), r.reload())} />}
      <FundingBox kind="equipment" id={x.id} funding={x.funding} refText={x.funding_ref} canEdit={!!r.data.canManage} onSaved={r.reload} sold={x.status === 'retired'} />
      <div className="grid-2">
        <Card title="Where it is">
          <p>
            <Badge tone={QT[x.status]} dot>
              {EQUIPMENT_STATUS_LABEL[x.status as EquipmentStatus]}
            </Badge>{' '}
            {x.assigned_name ?? (x.unit_number ? <Link to={at(`/fleet/${x.vehicle_id}`)}>Unit {x.unit_number}</Link> : null)}
          </p>
          {r.data.canManage && (
            <>
              <div className="row gap-sm wrap">
                {MOVES.map(([k, l]) => (
                  <button key={k} className={`btn sm ${to === k ? 'primary' : ''}`} onClick={() => (setTo(k), setTarget(''))}>
                    {l}
                  </button>
                ))}
              </div>
              {to && (
                <div className="row gap-sm wrap" style={{ marginTop: 8 }}>
                  {to === 'person' && (
                    <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Person">
                      <option value="">Who…</option>
                      {(staff.data ?? []).map((p: any) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {to === 'vehicle' && (
                    <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Vehicle">
                      <option value="">Which unit…</option>
                      {(cars.data ?? []).map((v: any) => (
                        <option key={v.id} value={v.id}>
                          {carName(v)}
                        </option>
                      ))}
                    </select>
                  )}
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" aria-label="Note" />
                  <button
                    className="btn primary"
                    disabled={a.busy || ((to === 'person' || to === 'vehicle') && !target)}
                    onClick={() => a.run(async () => (await api.post(`/api/equipment/${id}/move`, { to, userId: to === 'person' ? target : undefined, vehicleId: to === 'vehicle' ? target : undefined, note }), setTo(null), setNote(''), r.reload()))}
                  >
                    Save
                  </button>
                </div>
              )}
              <ErrorBox error={a.error} />
            </>
          )}
        </Card>
        <Card title="Details">
          <dl className="kv">
            <dt>Make / model</dt>
            <dd>{[x.make, x.model].filter(Boolean).join(' ') || '—'}</dd>
            <dt>Serial #</dt>
            <dd className="mono">{x.serial ?? '—'}</dd>
            <dt>Asset tag</dt>
            <dd className="mono">{x.asset_tag ?? '—'}</dd>
            <dt>PO #</dt>
            <dd>{x.po_number ?? '—'}</dd>
            <dt>In service</dt>
            <dd>{day(x.in_service_on)}</dd>
            <dt>Grant</dt>
            <dd>{grantText(x)}</dd>
            {x.notes && (
              <>
                <dt>Notes</dt>
                <dd className="pre">{x.notes}</dd>
              </>
            )}
          </dl>
        </Card>
      </div>
      <Card title="History">
        <History rows={r.data.history} />
      </Card>
    </div>
  );
}

export function SparesPage() {
  const { can } = useAuth();
  const r = useLoad(() => api.get<any>('/api/fleet/spares'), []);
  const [taking, setTaking] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [miles, setMiles] = useState('');
  const a = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const free = r.data.spares.filter((v: any) => !v.temp_user);
  const out = r.data.spares.filter((v: any) => v.temp_user);
  const mine = r.data.myTempCar;
  return (
    <div className="ops stack">
      <PageHeader crumb="Fleet & equipment" title="Spare cars" sub="Car in the shop? Take an available spare — it’s logged to you until you turn it back in." />
      <div className="ops-kpis">
        <KPI n={free.length} label="Available" tone={free.length ? 'ok' : 'warn'} />
        <KPI n={out.length} label="In use" />
        <KPI n={r.data.outOfService.length} label="Cars out of service" tone={r.data.outOfService.length ? 'warn' : undefined} />
      </div>
      <Card title="Available">
        {free.length === 0 ? (
          <p className="muted">No spares available right now.</p>
        ) : (
          <div className="spare-list">
            {free.map((v: any) => (
              <div key={v.id} className="spare">
                <div>
                  <strong>Unit {v.unit_number}</strong> {[v.year, v.color, v.make, v.model].filter(Boolean).join(' ')}
                  <div className="small muted">
                    {v.tag ? `Tag ${v.tag}` : ''} {v.mileage != null ? `· ${v.mileage.toLocaleString()} mi` : ''} {v.equipment ? `· ${v.equipment} items installed` : ''}
                  </div>
                </div>
                {!mine &&
                  (taking === v.id ? (
                    <div className="row gap-sm wrap">
                      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why — e.g. my car is in the shop" aria-label="Why" />
                      <input value={miles} onChange={(e) => setMiles(e.target.value.replace(/\D/g, ''))} placeholder="Mileage" inputMode="numeric" aria-label="Mileage" className="w-miles" />
                      <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/fleet/${v.id}/take`, { reason, mileage: miles ? Number(miles) : undefined }), setTaking(null), r.reload()))}>
                        Take it
                      </button>
                    </div>
                  ) : (
                    <button className="btn primary" onClick={() => (setTaking(v.id), setReason(''), setMiles(v.mileage ? String(v.mileage) : ''))}>
                      Assign it to me
                    </button>
                  ))}
              </div>
            ))}
          </div>
        )}
        <ErrorBox error={a.error} />
      </Card>
      <Card title="In use">
        {out.length === 0 ? (
          <p className="muted">No spares are out.</p>
        ) : (
          <div className="spare-list">
            {out.map((v: any) => (
              <ReturnRow key={v.id} v={v} mine={v.id === mine} canManage={can('fleet.manage') || can('schedule.manage')} onDone={r.reload} />
            ))}
          </div>
        )}
      </Card>
      {r.data.outOfService.length > 0 && (
        <Card title="Out of service">
          <ul className="plain">
            {r.data.outOfService.map((v: any) => (
              <li key={v.id}>
                <strong>Unit {v.unit_number}</strong> {[v.year, v.make, v.model].filter(Boolean).join(' ')}
                {v.assigned_name ? ` · ${v.assigned_name}` : ''} <span className="small muted">since {day(v.updated_at)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function ReturnRow({ v, mine, canManage, onDone }: { v: any; mine: boolean; canManage: boolean; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [miles, setMiles] = useState('');
  const a = useAction();
  return (
    <div className="spare">
      <div>
        <strong>Unit {v.unit_number}</strong> {[v.year, v.make, v.model].filter(Boolean).join(' ')} — <strong>{mine ? 'you' : v.temp_name}</strong>
        <div className="small muted">
          since {when(v.temp_since)}
          {v.temp_reason ? ` · ${v.temp_reason}` : ''}
        </div>
      </div>
      {(mine || canManage) &&
        (open ? (
          <div className="row gap-sm">
            <input value={miles} onChange={(e) => setMiles(e.target.value.replace(/\D/g, ''))} placeholder="Mileage now" inputMode="numeric" aria-label="Mileage now" className="w-miles" />
            <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/fleet/${v.id}/return`, { mileage: miles ? Number(miles) : undefined }), onDone()))}>
              Turn it in
            </button>
          </div>
        ) : (
          <button className="btn" onClick={() => setOpen(true)}>
            {mine ? 'Turn it back in' : 'Check it back in'}
          </button>
        ))}
      <ErrorBox error={a.error} />
    </div>
  );
}

export function MyGearPage() {
  const { me } = useAuth();
  const r = useLoad(() => api.get<any>('/api/me/gear'), []);
  const [ans, setAns] = useState<Record<string, { ok: boolean | null; note: string }>>({});
  const [name, setName] = useState('');
  const [certifying, setCertifying] = useState(false);
  const a = useAction();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  const g = r.data;
  const rows = [
    ...g.cars.map((c: any) => ({ key: `vehicle:${c.id}`, kind: 'vehicle', id: c.id, label: `Unit ${carName(c)}${c.spare ? ' (spare)' : ''}`, sub: [c.tag && `Tag ${c.tag}`, `VIN ${c.vin}`].filter(Boolean).join(' · ') })),
    ...g.items.map((q: any) => ({ key: `equipment:${q.id}`, kind: 'equipment', id: q.id, label: itemName(q), sub: [q.serial && `SN ${q.serial}`, q.asset_tag && `Tag ${q.asset_tag}`, q.unit_number && `in unit ${q.unit_number}`].filter(Boolean).join(' · ') })),
  ];
  const done = rows.every((x) => ans[x.key]?.ok === true || (ans[x.key]?.ok === false && ans[x.key]!.note.trim()));
  return (
    <div className="ops stack">
      <PageHeader crumb="Fleet & equipment" title="My gear" sub={`Everything issued to you. Certify it every ${g.everyDays} days.`} />
      <div className={`alert ${g.overdue ? 'warn' : 'ok'}`}>
        {g.lastCertified ? `Last certified ${day(g.lastCertified)}.` : 'You haven’t certified your inventory yet.'} {g.due && !g.overdue ? `Next due ${day(g.due)}.` : g.overdue ? 'It’s due now.' : ''}
      </div>
      {rows.length === 0 ? (
        <EmptyState icon="clipboard" title="Nothing is issued to you" />
      ) : (
        <Card
          title={certifying ? 'Check each item' : 'Issued to you'}
          actions={
            !certifying && (
              <button className="btn primary" onClick={() => setCertifying(true)}>
                Certify my inventory
              </button>
            )
          }
        >
          <div className="gear">
            {rows.map((x) => (
              <div key={x.key} className={`gear-row ${ans[x.key]?.ok === false ? 'bad' : ans[x.key]?.ok ? 'ok' : ''}`}>
                <div>
                  <strong>{x.label}</strong>
                  <div className="small muted mono">{x.sub}</div>
                </div>
                {certifying && (
                  <div className="gear-ans">
                    <button type="button" className={`btn sm ${ans[x.key]?.ok === true ? 'primary' : ''}`} onClick={() => setAns({ ...ans, [x.key]: { ok: true, note: '' } })} aria-pressed={ans[x.key]?.ok === true}>
                      I have it — OK
                    </button>
                    <button type="button" className={`btn sm ${ans[x.key]?.ok === false ? 'danger' : ''}`} onClick={() => setAns({ ...ans, [x.key]: { ok: false, note: ans[x.key]?.note ?? '' } })} aria-pressed={ans[x.key]?.ok === false}>
                      Problem
                    </button>
                    {ans[x.key]?.ok === false && (
                      <input value={ans[x.key]!.note} onChange={(e) => setAns({ ...ans, [x.key]: { ok: false, note: e.target.value } })} placeholder="What’s wrong? (missing, damaged…)" aria-label={`Problem with ${x.label}`} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {certifying && (
            <div className="stack-sm cert-sign">
              <p className="small">I certify that I have personally inspected each item listed above, that it is in my possession and in working order except as I have noted, and that this inventory is true and complete.</p>
              <div className="row gap-sm wrap">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`Type your name: ${me!.user.displayName}`} aria-label="Type your name to sign" />
                <button
                  className="btn primary"
                  disabled={!done || !name.trim() || a.busy}
                  onClick={() =>
                    a.run(async () => {
                      await api.post('/api/me/gear/certify', { items: rows.map((x) => ({ kind: x.kind, id: x.id, ok: ans[x.key]!.ok, note: ans[x.key]!.note || undefined })), signedName: name });
                      setCertifying(false);
                      setAns({});
                      r.reload();
                    })
                  }
                >
                  Sign and certify
                </button>
                <button className="btn ghost" onClick={() => setCertifying(false)}>
                  Cancel
                </button>
              </div>
              <ErrorBox error={a.error} />
            </div>
          )}
        </Card>
      )}
      {g.history.length > 0 && (
        <Card title="Past certifications">
          <ul className="plain">
            {g.history.map((h: any) => (
              <li key={h.id}>
                {day(h.certified_at)} — {h.items.length} items{h.problems ? <Badge tone="warn">{h.problems} problem(s)</Badge> : <Badge tone="ok">All OK</Badge>} <span className="small muted">signed {h.signed_name}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

export function CertsPage() {
  const r = useLoad(() => api.get<any>('/api/fleet/certs'), []);
  const narrow = useNarrow();
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner />;
  return (
    <div className="ops stack">
      <PageHeader crumb="Fleet & equipment" title="Inventory checks" sub={`Who has certified the gear issued to them (every ${r.data.everyDays} days), and the problems they reported.`} />
      <div className="ops-kpis">
        <KPI n={r.data.people.length} label="People with gear" />
        <KPI n={r.data.overdue} label="Due or never certified" tone={r.data.overdue ? 'warn' : 'ok'} />
        <KPI n={r.data.people.filter((p: any) => p.problemItems.length).length} label="Reported problems" tone={r.data.people.some((p: any) => p.problemItems.length) ? 'bad' : undefined} />
      </div>
      {r.data.people.length === 0 ? (
        <EmptyState icon="clipboard" title="Nobody has gear issued yet" />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  {!narrow && <th>Unit</th>}
                  <th>Issued</th>
                  <th>Last certified</th>
                  <th>Problems reported</th>
                </tr>
              </thead>
              <tbody>
                {r.data.people.map((p: any) => (
                  <tr key={p.id}>
                    <td>
                      {p.display_name}
                      {p.call_number && <span className="dir-call">{p.call_number}</span>}
                    </td>
                    {!narrow && <td>{p.unit ?? '—'}</td>}
                    <td>
                      {p.cars ? `${p.cars} car${p.cars > 1 ? 's' : ''}, ` : ''}
                      {p.items} item{p.items === 1 ? '' : 's'}
                    </td>
                    <td>
                      {p.certified_at ? day(p.certified_at) : <span className="muted">Never</span>}{' '}
                      {p.overdue ? <Badge tone="warn" dot>Due</Badge> : <Badge tone="ok">OK until {day(p.due)}</Badge>}
                    </td>
                    <td>{p.problemItems.length ? p.problemItems.map((x: any) => <div key={x.id} className="small">{x.label}: {x.note}</div>) : <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
